import { useQuery } from '@tanstack/react-query';

import {
  getV1Notifications,
  type NotificationItem,
  type Notifications,
} from '../../api/generated/notification-inapp';
import { mockNotifications } from './notifications.mock';

/**
 * Query hooks for the in-app Notifications feature.
 *
 * Thin wrappers over the generated `@hey-api` SDK (see AGENTS.md colocated
 * hooks pattern). String query keys are shared with the colocated mutations in
 * `notifications.mutations.ts` so cache invalidation stays coherent.
 */

export interface NotificationsFilters {
  page?: number;
  limit?: number;
  notificationClass?: string;
  status?: { read?: boolean };
  search?: string;
}

const isNotificationsMockEnabled = import.meta.env.VITE_NOTIFICATIONS_MOCK === 'true';

// Mirrors the backend `search` filter: case-insensitive "contains" on the
// title or summary field, with the term taken literally (no trimming).
const matchNotificationText = (notification: NotificationItem, searchTerm?: string) => {
  if (!searchTerm) return true;

  const normalizedSearchTerm = searchTerm.toLowerCase();
  const title = notification.fields?.title?.toLowerCase() ?? '';
  const summary = notification.fields?.summary?.toLowerCase() ?? '';

  return title.includes(normalizedSearchTerm) || summary.includes(normalizedSearchTerm);
};

const filterMockNotifications = (filters?: NotificationsFilters) => {
  return mockNotifications.filter((notification) => {
    const matchesSearch = matchNotificationText(notification, filters?.search);
    const matchesClass =
      !filters?.notificationClass || notification.notification_class === filters.notificationClass;
    const matchesStatus =
      typeof filters?.status?.read !== 'boolean' ||
      notification.status?.read === filters.status.read;

    return matchesSearch && matchesClass && matchesStatus;
  });
};

export function useNotifications(filters?: NotificationsFilters, enabled = true) {
  return useQuery({
    queryKey: ['notifications', isNotificationsMockEnabled ? 'mock' : 'api', filters ?? 'all'],
    queryFn: async (): Promise<Notifications> => {
      if (isNotificationsMockEnabled) {
        const page = filters?.page ?? 1;
        const limit = filters?.limit ?? 100;
        const filteredNotifications = filterMockNotifications(filters);
        const pageStart = (page - 1) * limit;

        return {
          page,
          limit,
          total: filteredNotifications.length,
          items: filteredNotifications.slice(pageStart, pageStart + limit),
        };
      }

      const { data } = await getV1Notifications({
        query: {
          page: (filters?.page ?? 1) - 1,
          limit: filters?.limit ?? 100,
          notification_class: filters?.notificationClass,
          status: filters?.status,
          search: filters?.search,
        },
        throwOnError: true,
      });
      return data;
    },
    enabled: isNotificationsMockEnabled || enabled,
    refetchOnMount: true,
  });
}

export function useUnreadNotificationsCount(searchTerm?: string, enabled = true) {
  return useQuery({
    queryKey: [
      'notifications',
      'unread-count',
      isNotificationsMockEnabled ? 'mock' : 'api',
      searchTerm || 'all',
    ],
    queryFn: async (): Promise<number> => {
      if (isNotificationsMockEnabled) {
        return mockNotifications.filter(
          (notification) =>
            !notification.status?.read && matchNotificationText(notification, searchTerm)
        ).length;
      }

      const { data } = await getV1Notifications({
        query: {
          page: 0,
          limit: 100,
          status: { read: false },
          search: searchTerm,
        },
        throwOnError: true,
      });
      return data.total;
    },
    enabled: isNotificationsMockEnabled || enabled,
    refetchOnMount: true,
  });
}
